import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { WalletIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useUserContext } from '../../../contexts/user/UserContext';
import WalletsListTab from './WalletsListTab';
import WalletsExportTab from './WalletsExportTab';

type WalletsTabKey = 'wallets' | 'export';

/**
 * Admin › Wallets — the Ancalagon-staff-only surface over the customers' prepaid wallets
 * (docs/api-types/admin-wallets.md): every balance (optionally at a cut-off date), and the
 * cross-client ledger export for the accountant. Per-client detail (ledger, top-ups, parked invoice
 * payments, adjustments) lives at /admin/wallets/:clientId.
 *
 * Gating is enforced in two independent places (defense in depth):
 *   1. AppLayout hides the whole "Admin" nav group unless `user.is_ancalagon_staff`.
 *   2. This page guards itself: a non-staff user who types /admin/wallets directly gets <NotFound />
 *      — the same response as a non-existent route. Both tabs defer their fetches on the same flag.
 *      The backend /admin routes enforce this authoritatively; this is the frontend mirror.
 */
export function Wallets() {
  const auth = useAuth();
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;
  const [activeTab, setActiveTab] = useState<WalletsTabKey>('wallets');

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }
  if (!isStaff) {
    return <NotFound />;
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem isActive>Wallets</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <WalletIcon />
          </Icon>
          Wallets
        </Content>
        <Content component="p">Prepaid balances held for customers, their ledgers and staff adjustments.</Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Tabs
          activeKey={activeTab}
          onSelect={(_event, tabKey) => setActiveTab(tabKey as WalletsTabKey)}
          aria-label="Wallet sections"
          mountOnEnter
        >
          <Tab eventKey="wallets" title={<TabTitleText>Wallets</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <WalletsListTab isStaff={isStaff} />
            </div>
          </Tab>
          <Tab eventKey="export" title={<TabTitleText>Ledger export</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <WalletsExportTab isStaff={isStaff} />
            </div>
          </Tab>
        </Tabs>
      </PageSection>
    </>
  );
}

export default Wallets;
